import { useTranslation } from "react-i18next";
import { X, Trash, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { useToast } from "@packages/contexts/ToastContext";
import { useQueryClient } from "@tanstack/react-query";
import Joyride, { CallBackProps, Step } from "react-joyride";
import { useNavigate } from "react-router-dom";
import { useLocalStorage } from "@packages/hooks/useLocalStorage";
import { useMobile } from "@packages/hooks/useMobile";
import { joyrideStyles, joyrideStylesDark } from "@packages/utils";
import { trackEvent } from "@packages/hooks/useAnalytics";
import {
  getGetUserRubricGeneratorsQueryKey,
  useDeleteRubricGenerator,
  useGetUserRubricGenerators,
} from "@packages/api/madlenAPI";
import { RubricCard } from "../../components/rubric/RubricCard";
import { RubricGeneratorResponseDto } from "@packages/api/madlenAPI.schemas";
import DeleteConfirmPopUp from "../../components/tool_components/DeleteConfirmPopUp";
import ErrorComponent from "../../components/error-component";
import { Button, Text } from "@core/index";
import ToolHelper from "@features/tools/components/ToolHelper";
import { useTheme } from "@packages/hooks/useTheme";

const RubricView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [runTour, setRunTour] = useState(false);
  const [skipAllTours] = useLocalStorage("skip-all-tours", false);
  const isMobile = useMobile();
  const [selectedRubric, setSelectedRubric] =
    useState<RubricGeneratorResponseDto | null>(null);
  const [showDeletePopup, setShowDeletePopup] = useState<number | undefined>(
    undefined,
  );
  const { addToast } = useToast();
  const {
    data: rubrics,
    isLoading,
    error,
    refetch,
  } = useGetUserRubricGenerators();
  const { theme } = useTheme();

  const rubricToDelete =
    rubrics?.data
      ?.find((rubric) => rubric.id === showDeletePopup)
      ?.title?.substring(0, 30)
      ?.replace(/\*/g, "")
      ?.replace(/\n/g, "")
      ?.replace(/#/g, "") || t("rubric.untitledRubric");

  const queryClient = useQueryClient();
  const queryKey = getGetUserRubricGeneratorsQueryKey();
  const { mutate: deleteRubric } = useDeleteRubricGenerator({
    mutation: {
      onMutate: async ({ id }) => {
        const previousData = queryClient.getQueryData(queryKey);
        // Optimistically update UI
        queryClient.setQueryData(queryKey, (oldData: any) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            data: oldData?.data?.filter(
              (rubric: RubricGeneratorResponseDto) => rubric.id !== id,
            ),
          };
        });

        return { previousData };
      },
      onError: (_error, _variables, context) => {
        if (context?.previousData) {
          queryClient.setQueryData(queryKey, context.previousData);
        }
        console.error("Failed to delete rubric");
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey });
      },
    },
  });

  const handleDelete = async (rubricId: number) => {
    try {
      await deleteRubric({ id: rubricId });
      addToast({
        title: t("rubric.deleted"),
        variant: "success",
        duration: 3000,
      });
    } catch (error) {
      addToast({
        title: t("rubric.deleteError"),
        description: t("rubric.deleteErrorDescription"),
        variant: "error",
      });
    }
  };

  const steps: Step[] = [
    {
      target: "body",
      content: (
        <div className="flex flex-col items-center gap-4">
          <video
            src="/tutorials/rubric.mp4"
            autoPlay
            loop
            muted
            className="w-[350px] rounded-xl"
          />
          <Text variant={"p"} className="text-center">
            {t("onboarding.rubric.tour.welcome")}
          </Text>
        </div>
      ),
      placement: "center",
      disableBeacon: true,
    },
    {
      target: ".create-button",
      content: t("onboarding.rubric.tour.create"),
      placement: "bottom-end",
    },
  ];

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status, action } = data;

    if (action === "close") {
      setRunTour(false);
      return;
    }

    if (status === "finished" || status === "skipped") {
      setRunTour(false);
      if (status === "finished") {
        navigate("/content/rubric/create");
      }
    }
  };

  useEffect(() => {
    const shouldShowTour =
      !isLoading &&
      (!rubrics?.data || rubrics.data.length === 0) &&
      !skipAllTours &&
      !isMobile;
    setRunTour(shouldShowTour);
  }, [isLoading, rubrics?.data, skipAllTours, isMobile]);

  const renderModal = () =>
    selectedRubric && (
      <div className="fixed inset-0 z-[9999] overflow-hidden bg-black bg-opacity-75 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-dark-600 rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col border border-dark-200 dark:border-dark-400">
          <div className="p-3 flex justify-end items-center border-b border-gray-200 dark:border-dark-400">
            <Button
              size="icon"
              rounded="full"
              onClick={() => setSelectedRubric(null)}
              bgColor="hover:bg-gray-100 dark:hover:bg-dark-700"
              className="w-10 h-10"
            >
              <X size={24} className="text-gray-500 dark:text-gray-400" />
            </Button>
          </div>

          <div className="flex-1 overflow-auto px-6 pb-6">
            <RubricCard
              rubric={selectedRubric}
              isImageLoading={false}
              onCreateAgain={() => navigate("/content/rubric/create")}
            />
          </div>
        </div>
      </div>
    );

  const LoadingState = () => (
    <div
      className="flex flex-col items-center justify-center min-h-[400px] space-y-4"
      data-testid="loading-dot-container"
    >
      <div className="w-16 h-16 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
      <Text variant={"p"} className="text-gray-600 dark:text-gray-400">
        {t("rubric.loading")}
      </Text>
    </div>
  );

  const { trackEvent: track } = trackEvent();
  const handleClick = () => {
    track(
      {
        category: "ContentGeneration",
        action: "rubric.generate",
        label: "Rubric generation click",
        nonInteraction: false,
      },
      {
        location: "/rubric/generate",
        element: "generate-button",
      },
    );
  };

  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center min-h-[400px] space-y-6 text-center">
      <div className="w-24 h-24 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
        <div className="w-12 h-12 bg-green-500 dark:bg-green-400 rounded-full flex items-center justify-center">
          <Plus size={24} className="text-white" />
        </div>
      </div>
      <div className="space-y-2">
        <Text variant="h3" className="text-gray-900 dark:text-gray-100">
          {t("rubric.empty.title")}
        </Text>
        <Text variant="p" className="text-gray-600 dark:text-gray-400 max-w-md">
          {t("rubric.empty.description")}
        </Text>
      </div>
      <Button
        onClick={() => navigate("/content/rubric/create")}
        size="lg"
        rounded="full"
        bgColor="bg-green-500 hover:bg-green-600 dark:bg-green-500 dark:hover:bg-green-600"
        textColor="text-white"
        className="create-button px-8"
      >
        {t("rubric.create.title")}
      </Button>
    </div>
  );

  return (
    <div className="flex-1 p-3 sm:p-4 md:p-6 lg:p-8 lg:mt-5 bg-gray-50 dark:bg-dark-500 lg:rounded-tl-3xl overflow-y-auto h-full custom-scrollbar">
      <Joyride
        locale={{
          back: t("onboarding.back"),
          close: t("onboarding.close"),
          last: t("onboarding.finish"),
          next: t("onboarding.next"),
          skip: t("onboarding.skip"),
        }}
        steps={steps}
        run={!skipAllTours && !isMobile && runTour}
        continuous
        showSkipButton
        styles={theme === "dark" ? joyrideStylesDark : joyrideStyles}
        callback={handleJoyrideCallback}
      />
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="mb-4 sm:mb-6 lg:mb-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4">
          <div className="min-w-0 flex-1">
            <Text
              variant="h1"
              className="text-lg sm:text-xl lg:text-2xl xl:text-3xl break-words"
            >
              {t("quickActions.rubricCreate")}
            </Text>
            <Text
              variant="p"
              className="text-gray-600 dark:text-gray-400 mt-1 text-sm sm:text-base break-words"
            >
              {t("quickActions.rubricCreateDesc")}
            </Text>
          </div>
          <div className="flex-shrink-0">
            <Button
              onClick={() => {
                handleClick();
                navigate("/content/rubric/create");
              }}
              size="md"
              rounded="full"
              bgColor="bg-green-500 hover:bg-green-600 dark:bg-green-500 dark:hover:bg-green-600"
              textColor="text-white"
              className="create-button px-6 sm:px-8"
              leftIcon={<Plus size={20} />}
            >
              {t("rubric.create.title")}
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-dark-200 dark:border-dark-400 p-6">
            <LoadingState />
          </div>
        ) : error ? (
          <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-dark-200 dark:border-dark-400 p-6">
            <ErrorComponent
              title={t("rubric.error.title")}
              refetch={refetch}
              description={t("rubric.error.description")}
              retryButton={t("rubric.error.retryButton")}
            />
          </div>
        ) : !rubrics?.data?.length ? (
          <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-dark-200 dark:border-dark-400 p-6">
            <EmptyState />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
            {rubrics?.data?.map((rubric) => (
              <div
                key={rubric.id}
                className="group cursor-pointer bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-dark-200 dark:border-dark-400 overflow-hidden transform transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
              >
                <div className="p-4 sm:p-6 h-full relative">
                  <Button
                    variant="ghost"
                    size="icon"
                    rounded="xl"
                    className="absolute top-3 right-3 z-10 opacity-0 group-hover:opacity-100 transition-all duration-200"
                    bgColor="bg-white/90 dark:bg-dark-500/90 hover:bg-red-50 dark:hover:bg-red-900/50"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDeletePopup(rubric.id);
                    }}
                  >
                    <Trash
                      size={16}
                      className="text-gray-400 dark:text-gray-500 group-hover:text-red-500 dark:group-hover:text-red-400 transition-colors"
                    />
                  </Button>

                  <div
                    onClick={() => setSelectedRubric(rubric)}
                    className="h-full"
                  >
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Text
                          variant="span"
                          className="text-xs text-gray-500 dark:text-gray-400"
                        >
                          {new Date(rubric.createdAt).toLocaleDateString()}
                        </Text>
                        {rubric.authorName && (
                          <Text
                            variant="span"
                            className="text-xs text-gray-500 dark:text-gray-400"
                          >
                            {rubric.authorName}
                          </Text>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        <div className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-lg text-xs font-medium">
                          {rubric.rubricType === "ANALYTIC"
                            ? t("rubric.type.analytic")
                            : t("rubric.type.holistic")}
                        </div>
                        {rubric.totalScore && (
                          <div className="px-2 py-1 bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-400 rounded-lg text-xs font-medium">
                            {rubric.totalScore} {t("rubric.points")}
                          </div>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Text
                          variant="h3"
                          className="text-base font-semibold text-gray-900 dark:text-gray-100 line-clamp-2"
                        >
                          {rubric.title || t("rubric.untitledRubric")}
                        </Text>
                        {rubric.assignmentInstructions && (
                          <Text
                            variant="p"
                            className="text-sm text-gray-600 dark:text-gray-400 line-clamp-3"
                          >
                            {rubric.assignmentInstructions}
                          </Text>
                        )}
                      </div>

                      {(rubric.criteria?.length > 0 ||
                        rubric.levelDescriptions?.length > 0) && (
                        <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-600">
                          <Text
                            variant="span"
                            className="text-xs text-gray-500 dark:text-gray-400"
                          >
                            {rubric.rubricType === "ANALYTIC"
                              ? t("rubric.criteriaCount", {
                                  count: rubric.criteria?.length || 0,
                                })
                              : t("rubric.levelsCount", {
                                  count: rubric.levelDescriptions?.length || 0,
                                })}
                          </Text>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal */}
        {renderModal()}

        <ToolHelper
          toolPath={"rubric"}
          shouldShowTour={
            !skipAllTours &&
            !isMobile &&
            rubrics?.data &&
            rubrics?.data?.length === 0
          }
          toolType={t(`sidebar.menu.toolbox.rubric`)}
        />

        {/* Delete Confirmation PopUp */}
        {showDeletePopup && (
          <DeleteConfirmPopUp
            title={t("rubric.confirmDelete", {
              title: rubricToDelete + "...",
            })}
            showDeletePopup={showDeletePopup}
            setShowDeletePopup={setShowDeletePopup}
            handleDelete={handleDelete}
          />
        )}
      </div>
    </div>
  );
};

export default RubricView;
